<template>
    <!-- แก้ไขข้อมูลบริษัท (บริษัทอื่นที่ไม่ใช่ company 1): Company information + Billing ในหน้าเดียว ไม่มี step -->
    <v-dialog :value="value" max-width="920px" persistent scrollable @input="$emit('input', $event)">
        <v-card class="acd">
            <!-- Header -->
            <div class="acd-header">
                <span class="acd-title">Edit company</span>
                <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
            </div>

            <v-card-text class="acd-body">
                <!-- ===================== Company information ===================== -->
                <h2 class="acd-h2">Company information</h2>
                <p class="acd-sub">Enter the company's legal name, address, and contact details.</p>

                <div class="acd-section-title">Company details</div>
                <v-row dense>
                    <v-col cols="12" md="8" class="acd-field">
                        <label>Company name <span class="req">*</span></label>
                        <v-text-field v-model="com.name" ref="Company_Name_Add" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label class="d-flex align-center">
                            Company code <span class="req ml-1">*</span>
                            <v-tooltip bottom max-width="320">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon small color="#3b82f6" class="ml-2" v-bind="attrs"
                                        v-on="on">mdi-help-circle</v-icon>
                                </template>
                                <span>Employees use this code to identify your company when signing in, so keep it
                                    unique and easy to recognise.</span>
                            </v-tooltip>
                        </label>
                        <v-text-field v-model="com.code" ref="Company_Code_Add" maxlength="6" :rules="comcodeRules"
                            disabled outlined dense hide-details="auto"></v-text-field>
                        <div class="acd-hint">Company code cannot be changed</div>
                    </v-col>
                </v-row>

                <div class="acd-section-title mt-3">Company address</div>
                <v-row dense>
                    <v-col cols="12" class="acd-field">
                        <label>Address line 1 <span class="req">*</span></label>
                        <v-text-field v-model="com.company_address" ref="Company_Address_Add" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" class="acd-field">
                        <label>Address line 2</label>
                        <v-text-field v-model="com.company_address2" outlined dense hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label>Sub-district</label>
                        <v-text-field v-model="com.subdistrict" ref="subdistrict" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label>District</label>
                        <v-text-field v-model="com.district" ref="district" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label>Province</label>
                        <v-text-field v-model="com.province" ref="province" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label>Postcode</label>
                        <v-text-field v-model="com.postcode" ref="postcode" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label>Country <span class="req">*</span></label>
                        <v-select v-model="com.country" ref="country" :items="countryList"
                            placeholder="Select a country" outlined dense hide-details="auto"></v-select>
                    </v-col>
                    <v-col cols="12" md="4" class="acd-field">
                        <label>Branch</label>
                        <v-text-field v-model="com.company_branch" ref="Company_Branch_Add" outlined dense
                            hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="acd-field">
                        <label>Company email</label>
                        <v-text-field v-model="com.company_email" ref="Company_Email_Add" :rules="emailRules" outlined
                            dense hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="acd-field">
                        <label>Telephone number</label>
                        <v-text-field v-model="com.company_tel" ref="Company_Telephone_Number_Add" :rules="telRules"
                            outlined dense hide-details="auto"></v-text-field>
                    </v-col>
                </v-row>

                <v-divider class="my-6"></v-divider>

                <!-- ===================== Billing & employee settings ===================== -->
                <h2 class="acd-h2">Billing &amp; employee settings</h2>
                <p class="acd-sub">Configure invoice details and employee ID generation.</p>
                <v-row>
                    <!-- Invoice -->
                    <v-col cols="12" md="7">
                        <div class="acd-panel">
                            <div class="acd-panel-title">Invoice information</div>
                            <v-checkbox v-model="sameAs" label="Same as company information" color="#2a7f86" dense
                                hide-details class="mt-0 mb-1"></v-checkbox>

                            <div class="acd-invoice-scroll">
                                <template v-if="sameAs">
                                    <div class="acd-hint mb-3">
                                        Company name, address, email, telephone, country, branch and postcode will
                                        be
                                        copied automatically.
                                    </div>
                                    <div class="acd-summary">
                                        <div class="font-weight-bold">{{ com.name || "-" }}</div>
                                        <div>{{ companyAddressLine || "-" }}</div>
                                        <div>{{ com.company_email || "-" }} · {{ com.company_tel || "-" }}</div>
                                    </div>
                                    <a class="acd-link" @click="sameAs = false">Use different billing details</a>
                                    <v-divider class="my-4"></v-divider>
                                </template>
                                <div v-else class="acd-hint mb-3">Enter separate company, address and contact
                                    details
                                    for billing.
                                </div>

                                <v-row dense>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Company name <span class="req">*</span></label>
                                        <v-text-field v-model="com.inv_company_name" ref="INV_Company_Name_Add" outlined
                                            dense hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6" class="acd-field">
                                        <label>Company name (Thai)</label>
                                        <v-text-field v-model="com.inv_company_name_th" ref="INV_Company_Name_TH_Add"
                                            outlined dense hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" class="acd-field">
                                        <label>Invoice address line 1 <span class="req">*</span></label>
                                        <v-text-field v-model="com.inv_company_address" ref="Invoice_Address_Add"
                                            outlined dense hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" class="acd-field">
                                        <label>Invoice address line 2</label>
                                        <v-text-field v-model="com.inv_company_address2" outlined dense
                                            hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Sub-district</label>
                                        <v-text-field v-model="com.inv_subdistrict" ref="inv_subdistrict" outlined dense
                                            hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>District</label>
                                        <v-text-field v-model="com.inv_district" ref="inv_district" outlined dense
                                            hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Province</label>
                                        <v-combobox v-model="com.inv_province" ref="inv_province" :items="provinceList"
                                            outlined dense hide-details="auto"></v-combobox>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Postcode</label>
                                        <v-text-field v-model="com.inv_postcode" ref="inv_postcode" outlined dense
                                            hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Country <span class="req">*</span></label>
                                        <v-combobox v-model="com.inv_country" ref="inv_country" :items="countryList"
                                            outlined dense hide-details="auto"></v-combobox>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Branch</label>
                                        <v-text-field v-model="com.inv_company_branch" ref="INV_Branch_Add" outlined
                                            dense hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Company email</label>
                                        <v-text-field v-model="com.inv_company_email" ref="INV_Email_Add"
                                            :rules="emailRules" outlined dense hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col v-show="!sameAs" cols="12" md="6" class="acd-field">
                                        <label>Telephone number</label>
                                        <v-text-field v-model="com.inv_company_tel" ref="INV_Telephone_Number_Add"
                                            :rules="telRules" outlined dense hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6" class="acd-field">
                                        <label>Tax ID <span class="req">*</span></label>
                                        <v-text-field v-model="com.inv_tax_id" ref="INV_Tax_ID_Add" outlined dense
                                            hide-details="auto"></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6" class="acd-field">
                                        <label>Status</label>
                                        <v-select v-model="com.status" :items="statusall" :disabled="disabledStatus"
                                            outlined dense hide-details></v-select>
                                    </v-col>
                                </v-row>
                                <div v-if="sameAs" class="acd-hint mt-3">
                                    Turn off "Same as company information" to edit the full invoice address.
                                </div>
                            </div>
                        </div>
                    </v-col>

                    <!-- Employee ID -->
                    <v-col cols="12" md="5">
                        <div class="acd-panel">
                            <div class="acd-panel-title">Employee ID settings</div>
                            <div class="acd-field">
                                <label>Assign employee ID</label>
                                <v-select v-model="com.doc_status" :items="emp_assign" item-text="name"
                                    item-value="values" :disabled="disabledStatus" outlined dense
                                    hide-details></v-select>
                            </div>
                            <template v-if="com.doc_status == 0">
                                <div class="acd-field mt-4">
                                    <label>Prefix</label>
                                    <v-text-field v-model="com.id_prefix" outlined dense hide-details></v-text-field>
                                </div>
                                <v-row dense class="mt-2">
                                    <v-col cols="6" class="acd-field">
                                        <label>Range</label>
                                        <v-text-field v-model="com.running_len" ref="Range" type="number" outlined dense
                                            hide-details></v-text-field>
                                    </v-col>
                                    <v-col cols="6" class="acd-field">
                                        <label>Next running</label>
                                        <v-text-field v-model="com.running_next" ref="Next Running" type="number"
                                            outlined dense hide-details></v-text-field>
                                    </v-col>
                                </v-row>
                                <div class="acd-preview mt-4">
                                    <div class="acd-hint">Employee ID preview</div>
                                    <div class="acd-preview-id">{{ employeeIdPreview }}</div>
                                </div>
                                <div class="acd-hint mt-2">New employee IDs will continue from this number.</div>
                            </template>
                            <div v-else class="acd-hint mt-3">Employee IDs will be entered manually when adding
                                employees.</div>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Footer -->
            <div class="acd-footer">
                <v-spacer></v-spacer>
                <v-btn outlined class="acd-btn" @click="close">Cancel</v-btn>
                <v-btn depressed color="#2a7f86" class="acd-btn white--text ml-3" @click="save">Save changes</v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import api from "@/services/api";

const emptyCompany = () => ({
    code: "",
    name: "",
    company_address: "",
    company_address2: "",
    subdistrict: "",
    district: "",
    province: "",
    postcode: "",
    country: "",
    company_branch: "",
    company_email: "",
    company_tel: "",
    inv_company_name: "",
    inv_company_name_th: "",
    inv_company_address: "",
    inv_company_address2: "",
    inv_subdistrict: "",
    inv_district: "",
    inv_province: "",
    inv_postcode: "",
    inv_country: "",
    inv_company_branch: "",
    inv_company_email: "",
    inv_company_tel: "",
    inv_tax_id: "",
    status: "ACTIVE",
    module: "Employee",
    id_prefix: "",
    running_year: new Date().getFullYear().toString().slice(-2),
    running_len: 1,
    running_next: 1,
    doc_status: 0,
});

// รายชื่อจังหวัด (ใช้ใน dropdown Province, พิมพ์ค่าอื่นเองได้)
const PROVINCES = [
    "Bangkok", "Amnat Charoen", "Ang Thong", "Bueng Kan", "Buriram", "Chachoengsao", "Chai Nat",
    "Chaiyaphum", "Chanthaburi", "Chiang Mai", "Chiang Rai", "Chonburi", "Chumphon", "Kalasin",
    "Kamphaeng Phet", "Kanchanaburi", "Khon Kaen", "Krabi", "Lampang", "Lamphun", "Loei", "Lopburi",
    "Mae Hong Son", "Maha Sarakham", "Mukdahan", "Nakhon Nayok", "Nakhon Pathom", "Nakhon Phanom",
    "Nakhon Ratchasima", "Nakhon Sawan", "Nakhon Si Thammarat", "Nan", "Narathiwat", "Nong Bua Lamphu",
    "Nong Khai", "Nonthaburi", "Pathum Thani", "Pattani", "Phang Nga", "Phatthalung", "Phayao",
    "Phetchabun", "Phetchaburi", "Phichit", "Phitsanulok", "Phra Nakhon Si Ayutthaya", "Phrae", "Phuket",
    "Prachinburi", "Prachuap Khiri Khan", "Ranong", "Ratchaburi", "Rayong", "Roi Et", "Sa Kaeo",
    "Sakon Nakhon", "Samut Prakan", "Samut Sakhon", "Samut Songkhram", "Saraburi", "Satun", "Sing Buri",
    "Sisaket", "Songkhla", "Sukhothai", "Suphan Buri", "Surat Thani", "Surin", "Tak", "Trang", "Trat",
    "Ubon Ratchathani", "Udon Thani", "Uthai Thani", "Uttaradit", "Yala", "Yasothon",
];

const COUNTRIES = [
    "Thailand", "Cambodia", "China", "Hong Kong", "India", "Indonesia", "Japan", "Laos", "Malaysia",
    "Myanmar", "Philippines", "Singapore", "South Korea", "Taiwan", "Vietnam", "Australia",
    "United Kingdom", "United States",
];

// ฟิลด์ invoice ที่ถูก copy มาจากข้อมูลบริษัทเมื่อติ๊ก "Same as company information"
const COPIED_INVOICE_REFS = [
    "INV_Company_Name_Add",
    "Invoice_Address_Add",
    "inv_subdistrict",
    "inv_district",
    "inv_province",
    "inv_postcode",
    "inv_country",
    "INV_Branch_Add",
    "INV_Email_Add",
    "INV_Telephone_Number_Add",
];

// ฟิลด์ที่ส่งไป api.updateCompany (เหมือน dialog edit เดิม — ไม่ส่ง code)
const UPDATE_COMPANY_FIELDS = [
    "name", "company_address", "company_branch", "company_email", "company_tel",
    "inv_company_name", "inv_company_name_th", "inv_company_address", "inv_company_branch",
    "inv_company_email", "inv_company_tel", "inv_tax_id", "status",
    "company_address2", "subdistrict", "district", "province", "postcode", "country",
    "inv_company_address2", "inv_subdistrict", "inv_district", "inv_province", "inv_postcode", "inv_country",
];

export default {
    name: "EditCompanyDialog",
    props: {
        value: { type: Boolean, default: false },
        disabledStatus: { type: Boolean, default: false },
        company: { type: Object, default: null }, // แถวจากตาราง company
    },
    data: () => ({
        com: emptyCompany(),
        sameAs: false,
        statusall: ["ACTIVE", "INACTIVE"],
        provinceList: PROVINCES,
        countryList: COUNTRIES,
        emp_assign: [
            { name: "Manual", values: 1 },
            { name: "Auto-generated", values: 0 },
        ],
        emailRules: [
            (v) =>
                !v ||
                /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) ||
                "Please enter a valid email address",
        ],
        telRules: [(v) => !v || /^[+]?([0-9,-]+)$/.test(v) || "Please Input Number Only"],
        comcodeRules: [(v) => (!v || v.length > 6 ? "Please check that the Company Code" : true)],
    }),
    computed: {
        companyId() {
            return this.company ? Number(this.company.id) : null;
        },
        companyAddressLine() {
            const c = this.com;
            return [c.company_address, c.company_address2, c.subdistrict, c.district, c.province, c.postcode, c.country]
                .map((s) => (s || "").toString().trim())
                .filter(Boolean)
                .join(", ");
        },
        // ⚠️ รูปแบบนี้เป็นตัวอย่างสำหรับแสดงผลเท่านั้น
        employeeIdPreview() {
            const len = Math.max(parseInt(this.com.running_len, 10) || 1, 1);
            const next = Math.max(parseInt(this.com.running_next, 10) || 1, 1);
            const num = String(next).padStart(len, "0");
            const prefix = (this.com.id_prefix || "").trim();
            return prefix ? `${prefix}-${num}` : num;
        },
    },
    watch: {
        value(open) {
            if (open) this.load();
        },
        sameAs(val) {
            if (val) this.copyCompanyToInvoice();
        },
    },
    methods: {
        async load() {
            this.sameAs = false;
            const it = this.company || {};
            const com = emptyCompany();
            Object.keys(com).forEach((k) => {
                if (it[k] !== undefined && it[k] !== null) com[k] = it[k];
            });
            this.com = com;

            // Employee ID settings
            this.$showLoader();
            try {
                const resDoc = await api.fineDocRunningByModlue("Employee", this.companyId);
                const d = resDoc && resDoc.data;
                if (d) {
                    Object.assign(this.com, {
                        id_prefix: d.id_prefix || "",
                        running_year: d.running_year,
                        running_len: d.running_len,
                        running_next: d.running_next,
                        doc_status: d.check_doc_auto,
                    });
                }
            } catch (error) {
                console.error("EditCompanyDialog load doc running error:", error);
            } finally {
                this.$hideLoader();
            }
        },

        close() {
            this.$emit("input", false);
        },

        copyCompanyToInvoice() {
            const c = this.com;
            const t = (v) => (v || "").toString().trim();
            c.inv_company_name = t(c.name);
            c.inv_company_address = t(c.company_address);
            c.inv_company_address2 = t(c.company_address2);
            c.inv_subdistrict = t(c.subdistrict);
            c.inv_district = t(c.district);
            c.inv_province = t(c.province);
            c.inv_postcode = t(c.postcode);
            c.inv_country = t(c.country);
            c.inv_company_branch = t(c.company_branch);
            c.inv_company_email = t(c.company_email);
            c.inv_company_tel = t(c.company_tel);
        },

        // กันซ้ำ: ไม่นับแถวของบริษัทตัวเอง
        othersOnly(rows) {
            const list = Array.isArray(rows) ? rows : [];
            return list.filter((r) => Number(r.id) !== this.companyId);
        },

        // ---------------- Validation (เหมือน AddCompanyDialog step 2 + 3) ----------------
        checks() {
            const emailRegex = /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            const telRegex = /^[+]?([0-9,-]+)$/;
            const c = this.com;
            const t = (v) => (v || "").toString().trim();

            const list = [
                // Company information
                { test: () => t(c.name) !== "", ref: "Company_Name_Add", msg: "Please Check Company Name Data" },
                { test: () => t(c.company_address) !== "", ref: "Company_Address_Add", msg: "Please check the company information again." },
                { test: () => t(c.country) !== "", ref: "country", msg: "Please select a country" },
                {
                    test: () => !t(c.company_email) || emailRegex.test(t(c.company_email)),
                    ref: "Company_Email_Add",
                    msg: "Please Check Company Email Data",
                },
                {
                    test: async () =>
                        !t(c.company_email) ||
                        this.othersOnly((await api.getCompanyCol(t(c.company_email), "company_email")).data).length === 0,
                    ref: "Company_Email_Add",
                    msg: "Email Duplicated, Please Check Email",
                },
                {
                    test: () => !t(c.company_tel) || telRegex.test(t(c.company_tel)),
                    ref: "Company_Telephone_Number_Add",
                    msg: "Please Check Telephone Number Data",
                },
                // Billing
                { test: () => t(c.inv_company_name) !== "", ref: "INV_Company_Name_Add", msg: "Please Check Company Name Data" },
                { test: () => t(c.inv_company_address) !== "", ref: "Invoice_Address_Add", msg: "Please check the invoice information again." },
                { test: () => t(c.inv_country) !== "", ref: "inv_country", msg: "Please check the invoice information again." },
                {
                    test: () => !t(c.inv_company_email) || emailRegex.test(t(c.inv_company_email)),
                    ref: "INV_Email_Add",
                    msg: "Please Check Company Email Data",
                },
                {
                    test: async () =>
                        !t(c.inv_company_email) ||
                        this.othersOnly((await api.getCompanyCol(t(c.inv_company_email), "inv_company_email")).data).length === 0,
                    ref: "INV_Email_Add",
                    msg: "Email Duplicated, Please Check Email",
                },
                {
                    test: () => !t(c.inv_company_tel) || telRegex.test(t(c.inv_company_tel)),
                    ref: "INV_Telephone_Number_Add",
                    msg: "Please Check Telephone Number Data",
                },
                { test: () => t(c.inv_tax_id) !== "", ref: "INV_Tax_ID_Add", msg: "Please Check Tax ID Data" },
            ];
            if (c.doc_status == 0) {
                list.push(
                    { test: () => Number(c.running_len) >= 1, ref: "Range", msg: "Please Check Range Data" },
                    { test: () => Number(c.running_next) >= 1, ref: "Next Running", msg: "Please Check Next Running Data" },
                );
            }
            return list;
        },

        async validate() {
            if (this.sameAs) this.copyCompanyToInvoice();
            for (const check of this.checks()) {
                if (!(await check.test())) {
                    if (this.sameAs && COPIED_INVOICE_REFS.includes(check.ref)) {
                        this.sameAs = false; // เปิดฟอร์ม invoice เต็มเพื่อให้แก้ช่องที่ผิดได้
                    }
                    this.fail(check.msg, check.ref);
                    return false;
                }
            }
            return true;
        },

        fail(msg, ref) {
            this.$emit("alert", { title: "Failed", message: msg, color: "text-h5 red--text text-center" });
            this.$nextTick(() => {
                const el = this.$refs[ref];
                if (el && el.focus) el.focus();
            });
        },

        // ---------------- Save (API เดิมเหมือนปุ่ม edit เดิม) ----------------
        async save() {
            this.$showLoader();
            try {
                if (!(await this.validate())) return;

                const c = this.com;
                const formData = new FormData();
                UPDATE_COMPANY_FIELDS.forEach((k) => {
                    const v = c[k];
                    formData.append(k, v === null || v === undefined ? "" : v.toString().trim());
                });

                const resUpdate = await api.updateCompany(this.companyId, formData);
                if (!(resUpdate.status == 200 || resUpdate.status == 201)) {
                    throw new Error(`updateCompany status ${resUpdate.status}`);
                }
                await api.updateDocRunningCom(this.companyId, {
                    module: "Employee",
                    id_prefix: c.id_prefix,
                    running_year: c.running_year,
                    running_len: c.running_len,
                    running_next: c.running_next,
                    doc_status: c.doc_status,
                });

                this.$emit("alert", {
                    title: "Success",
                    message: "Update data success",
                    color: "text-h5 green--text text-center",
                });
                this.$emit("saved");
                this.close();
            } catch (error) {
                console.error("EditCompanyDialog save error:", error);
                this.$emit("alert", {
                    title: "Failed",
                    message: "Update data Failed",
                    color: "text-h5 red--text text-center",
                });
            } finally {
                this.$hideLoader();
            }
        },
    },
};
</script>

<style scoped>
.acd {
    border-radius: 14px !important;
    color: #16253d;
}

.acd-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 22px 12px;
    border-bottom: 1px solid #e5e7eb;
}

.acd-title {
    font-size: 1.2rem;
    font-weight: 700;
}

/* Stepper */
.acd-stepper {
    display: flex;
    align-items: center;
    padding: 12px 22px;
    border-bottom: 1px solid #f1f1f1;
}

.acd-step {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.acd-step.clickable {
    cursor: pointer;
}

.acd-step-circle {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
    border-radius: 50%;
    background: #9ca3af;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
}

.acd-step-circle.active,
.acd-step-circle.done {
    background: #2a7f86;
}

.acd-step-label {
    margin-left: 8px;
    font-size: 0.85rem;
    color: #4b5563;
    white-space: nowrap;
}

.acd-step-label.active {
    color: #2a7f86;
    font-weight: 600;
}

.acd-step-line {
    flex: 1;
    height: 2px;
    background: #d1d5db;
    margin: 0 12px;
    min-width: 16px;
}

.acd-step-line.done {
    background: #2a7f86;
}

/* Body */
.acd-body {
    padding: 16px 22px !important;
    font-size: 0.875rem;
    color: #16253d !important;
}

.acd-h2 {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 4px;
}

.acd-sub {
    color: #6b7280;
    font-size: 0.85rem;
    margin-bottom: 12px;
}

.acd-field label {
    display: block;
    font-size: 0.8rem;
    color: #16253d;
    margin-bottom: 4px;
}

.req {
    color: #e53935;
}

.acd-section-title {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 6px;
}

.acd-counter {
    font-size: 0.75rem;
    color: #6b7280;
}

.acd-hint {
    font-size: 0.75rem;
    color: #6b7280;
}

.acd-field .acd-hint {
    margin-top: 6px;
}

.acd-info {
    display: flex;
    align-items: center;
    width: 100%;
    background: #f1f4f8;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.8rem;
    color: #374151;
}

.acd-panel {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
    height: 100%;
}

.acd-invoice-scroll {
    max-height: 380px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;
}

.acd-invoice-scroll::-webkit-scrollbar {
    width: 6px;
}

.acd-invoice-scroll::-webkit-scrollbar-thumb {
    background: #c4c9d0;
    border-radius: 6px;
}

.acd-panel-title {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.acd-summary {
    background: #f1f4f8;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.8rem;
    line-height: 1.6;
    margin-bottom: 10px;
}

.acd-link {
    color: #2a7f86 !important;
    text-decoration: underline;
    cursor: pointer;
}

.acd-preview {
    background: #f1f4f8;
    border-radius: 8px;
    padding: 10px 14px;
}

.acd-preview-id {
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 0.5px;
}

/* Menu access */
.acd-badge {
    background: #e0f2f1;
    color: #2a7f86;
    font-weight: 600;
    border-radius: 8px;
    font-size: 0.8rem;
    padding: 6px 12px;
}

.acd-menu {
    display: flex;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    min-height: 300px;
}

.acd-menu-left {
    width: 45%;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
}

.acd-menu-right {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.acd-menu-head {
    background: #f8fafc;
    padding: 10px 16px;
    font-size: 0.85rem;
    font-weight: 700;
    border-bottom: 1px solid #e5e7eb;
}

.acd-menu-scroll {
    flex: 1;
    overflow-y: auto;
    max-height: 300px;
}

.acd-group-row {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #f1f1f1;
    cursor: pointer;
}

.acd-group-row:hover {
    background: #f8fafc;
}

.acd-group-row.active {
    background: #e6f2f2;
}

.acd-group-name {
    flex: 1;
    margin-left: 8px;
}

.acd-count {
    color: #4b5563;
    font-size: 0.75rem;
}

.acd-suball {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #e5e7eb;
}

.acd-subgrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
}

.acd-subitem {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #f1f1f1;
    cursor: pointer;
}

.acd-subitem:nth-child(odd) {
    border-right: 1px solid #f1f1f1;
}

.acd-menu-foot {
    padding: 8px 16px;
    font-size: 0.75rem;
    color: #6b7280;
    border-top: 1px solid #e5e7eb;
}

/* Footer */
.acd-footer {
    display: flex;
    align-items: center;
    padding: 12px 22px;
    border-top: 1px solid #e5e7eb;
}

.acd-btn {
    text-transform: none !important;
    font-weight: 600;
    letter-spacing: normal;
    height: 36px !important;
    min-width: 90px !important;
    font-size: 0.85rem;
    border-radius: 8px;
}

/* ย่อ input / checkbox ของ Vuetify ภายใน dialog */
.acd ::v-deep .v-text-field input,
.acd ::v-deep .v-select__selection {
    font-size: 0.85rem;
}

.acd ::v-deep .v-input--dense>.v-input__control>.v-input__slot {
    min-height: 36px !important;
}

.acd ::v-deep .v-input--checkbox .v-label {
    font-size: 0.85rem;
}

.acd ::v-deep .v-messages {
    font-size: 0.7rem;
}

@media (max-width: 760px) {
    .acd-step-label {
        display: none;
    }

    .acd-menu {
        flex-direction: column;
    }

    .acd-menu-left {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #e5e7eb;
    }

    .acd-subgrid {
        grid-template-columns: 1fr;
    }
}
</style>